import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';

import DurationPicker, { DurationPickerProps } from '@components/DurationPicker';
import { DurationSelectorUnitFieldChangeEvent } from '@components/DurationSelector';
import useLanguage from '@hooks/useLanguage';
import useTranslate from '@hooks/useTranslate';
// UIKit Components
import { FlexBox } from '@spazious/react-components';
import parseDate from '@utils/parseDate';

import DateRangeDurationPickerInput from './DateRangeDurationPickerInput.js';

import '~react-datepicker/dist/react-datepicker.css';

/**
 * Possible date range formats:
 * StartDate/CheckIn/DurationValue
 * - 2021-09-01T09:00:00/2021-09-01T10:00:00
 * StartDate/CheckIn
 * - 2021-09-01T09:00:00
 * StartDate
 * - 2021-09-01
 * None
 * - null
 */
const formatDateRange = (startDate: Date | null, checkIn: Date | null, duration: string | null) => {
  const startDateRange = dayjs(startDate);
  const startDateRangeWithTime =
    startDateRange && checkIn
      ? startDateRange
          .set('hour', dayjs(checkIn).get('hour'))
          .set('minute', dayjs(checkIn).get('minute'))
      : null;

  const endRangeDate =
    startDateRangeWithTime && duration
      ? startDateRangeWithTime.clone().add(dayjs.duration(duration))
      : null;

  let result = null;

  if (startDateRangeWithTime) {
    result = startDateRangeWithTime.format('YYYY-MM-DDTHH:mm:ss');
  } else if (startDateRange) {
    result = startDateRange.format('YYYY-MM-DD');
  }

  if (endRangeDate) {
    result += `/${endRangeDate.format('YYYY-MM-DDTHH:mm:ss')}`;
  }

  return result;
};

export const parseDateRange = (value: string | null) => {
  const [startDateJSON, endDateJSON] = value ? value.split('/') : [null, null];
  const startDate = startDateJSON ? dayjs(startDateJSON).toDate() : null;
  const checkIn =
    startDateJSON && startDateJSON.split('T')[1] ? dayjs(startDateJSON).toDate() : null;
  const durationValue =
    startDateJSON && endDateJSON
      ? dayjs.duration(dayjs(endDateJSON).diff(dayjs(startDateJSON))).toJSON()
      : null;

  return { startDate, checkIn, durationValue };
};

const pickerControls = ['datePicker', 'timePicker', 'durationPicker'] as const;

export type DateRangeDurationPickerControl = (typeof pickerControls)[number];

const pickerControlsMap: Record<
  DateRangeDurationPickerControl,
  keyof ReturnType<typeof parseDateRange>
> = {
  datePicker: 'startDate',
  timePicker: 'checkIn',
  durationPicker: 'durationValue',
};

// TODO fix FlexBoxProps
export type DateRangeDurationPickerProps = /*FlexBoxProps &*/ {
  name?: string;
  autoFocus?: boolean;
  value?: string | null;
  dateFormat?: string;
  datePickerFormat?: string;
  timePickerFormat?: string;
  unitsProps?: DurationPickerProps['unitsProps'];
  borderless?: boolean;
  open?: boolean | DateRangeDurationPickerControl;
  minDate?: string;
  onChange?: (e: null | DurationSelectorUnitFieldChangeEvent, value: string | null) => void;
  onOpen?: (pickerControl: DateRangeDurationPickerControl) => void;
  onClose?: () => void;
  isDisabled?: boolean;
};

/**
 *
 * @returns
 */
export const DateRangeDurationPicker: React.FC<DateRangeDurationPickerProps> = ({
  name,
  autoFocus,
  open: openProp = false,
  value,
  borderless,
  datePickerFormat = 'dd/MM/yyyy',
  timePickerFormat = 'HH:mm',
  unitsProps,
  minDate,
  onChange,
  onOpen,
  onClose,
  isDisabled,
  ...restProps
}) => {
  const dateRangePickerRef = useRef<HTMLInputElement>(null);
  const [stateOpenPicker, setStateOpenPicker] = useState<DateRangeDurationPickerControl | false>(
    false
  );

  const parsedMinDate = minDate ? parseDate(minDate).toDate() : parseDate(new Date()).toDate();
  const { locale } = useLanguage();

  const parsedDateRange = useMemo(() => parseDateRange(value ?? null), [value]);

  const translate = useTranslate();

  const fireChange = useCallback(
    (
      evt: DurationSelectorUnitFieldChangeEvent | null,
      fieldName: keyof ReturnType<typeof parseDateRange>,
      value: Date | string | null
    ) => {
      const { startDate, checkIn, durationValue }: ReturnType<typeof parseDateRange> = {
        ...parsedDateRange,
        [fieldName]: value,
      };

      onChange?.(evt, formatDateRange(startDate, checkIn, durationValue));
    },
    [onChange, parsedDateRange]
  );

  const getPickerToOpen = useCallback(
    (
      pickerControl?: DateRangeDurationPickerControl | boolean
    ): DateRangeDurationPickerControl | boolean => {
      if (typeof pickerControl === 'string') {
        return pickerControl;
      }

      if (typeof pickerControl === 'boolean') {
        return pickerControl ? getPickerToOpen() : false;
      }

      for (let i = 0; i < pickerControls.length; i++) {
        const pickerName = pickerControls[i];
        const pickerKey = pickerControlsMap[pickerName];
        const pickerValue = parsedDateRange[pickerKey];

        if (!pickerValue) {
          return pickerName;
        }
      }

      return false;
    },
    [parsedDateRange]
  );

  const handlePickerOpenClose = useCallback(
    (pickerControl: DateRangeDurationPickerControl, open: boolean) => {
      const openValue = open ? pickerControl : open;
      if (openProp !== undefined) {
        if (openValue) {
          onOpen?.(openValue);
        } else {
          onClose?.();
        }
      } else {
        setStateOpenPicker(openValue);
      }
    },
    [onClose, onOpen, openProp]
  );

  const handleNextPickerOpenClose = useCallback(
    (pickerControl: DateRangeDurationPickerControl, open: boolean) => {
      const nextPickerControl = getPickerToOpen();
      if (!open && typeof nextPickerControl === 'string') {
        handlePickerOpenClose(nextPickerControl, true);
      } else {
        handlePickerOpenClose(pickerControl, open);
      }
    },
    [getPickerToOpen, handlePickerOpenClose]
  );

  const bindPickerOpenCloseHandler = useCallback(
    (pickerControl: DateRangeDurationPickerControl, open: boolean) => () =>
      handleNextPickerOpenClose(pickerControl, open),
    [handleNextPickerOpenClose]
  );

  const handleStartDateChange = useCallback<PickProp<ReactDatePickerProps, 'onChange'>>(
    (date) => {
      fireChange(null, 'startDate', date);
    },
    [fireChange]
  );

  const handleCheckInChange = useCallback<PickProp<ReactDatePickerProps, 'onChange'>>(
    (date) => {
      fireChange(null, 'checkIn', date);
      handleNextPickerOpenClose('timePicker', false);
    },
    [fireChange, handleNextPickerOpenClose]
  );

  const handleDurationChange = useCallback<PickProp<DurationPickerProps, 'onChange'>>(
    (evt, duration) => {
      fireChange(evt, 'durationValue', duration);
    },
    [fireChange]
  );

  useEffect(() => {
    if (autoFocus && dateRangePickerRef.current) {
      dateRangePickerRef.current.focus();
      handlePickerOpenClose('datePicker', true);
    }
  }, [autoFocus, handlePickerOpenClose]);

  const openPicker = openProp !== undefined ? getPickerToOpen(openProp) : stateOpenPicker;

  const pickerNodes = useMemo(
    () => [
      <DatePicker
        key={`${name}datePicker`}
        autoFocus={autoFocus}
        customInput={
          <DateRangeDurationPickerInput
            borderless={borderless}
            inputRef={dateRangePickerRef}
            inputStyle={{ minWidth: '100px', color: 'var(--primary-text)' }}
          />
        }
        dateFormat={datePickerFormat}
        disabled={isDisabled}
        locale={locale}
        minDate={parsedMinDate}
        name={`${name}datePicker`}
        open={openPicker === 'datePicker'}
        placeholderText={translate('Start Date')}
        popperPlacement="bottom-start"
        selected={parsedDateRange.startDate}
        showPopperArrow={false}
        startDate={parsedDateRange.startDate}
        onCalendarClose={bindPickerOpenCloseHandler('datePicker', false)}
        onCalendarOpen={bindPickerOpenCloseHandler('datePicker', true)}
        onChange={handleStartDateChange}
      />,

      <DatePicker
        key={`${name}timePicker`}
        showTimeSelect
        showTimeSelectOnly
        customInput={
          <DateRangeDurationPickerInput
            borderless={borderless}
            inputStyle={{ color: 'var(--primary-text)' }}
          />
        }
        dateFormat={timePickerFormat}
        disabled={!parsedDateRange.startDate}
        locale={locale}
        name={`${name}timePicker`}
        open={openPicker === 'timePicker'}
        placeholderText={translate('Check In Time')}
        popperPlacement="bottom"
        selected={parsedDateRange.checkIn}
        showPopperArrow={false}
        timeCaption={translate('Check in')}
        timeIntervals={60}
        onBlur={bindPickerOpenCloseHandler('timePicker', false)}
        onChange={handleCheckInChange}
        onClickOutside={bindPickerOpenCloseHandler('timePicker', false)}
        onFocus={bindPickerOpenCloseHandler('timePicker', true)}
        onInputClick={bindPickerOpenCloseHandler('timePicker', true)}
      />,

      <DurationPicker
        key={`${name}durationPicker`}
        borderless={borderless}
        disabled={!parsedDateRange.startDate || !parsedDateRange.checkIn}
        inputComponent={DateRangeDurationPickerInput}
        inputStyle={{ color: 'var(--primary-text)' }}
        name={`${name}durationPicker`}
        open={openPicker === 'durationPicker'}
        placeholder={translate('Duration')}
        units="hm"
        unitsProps={unitsProps}
        value={parsedDateRange.durationValue}
        onChange={handleDurationChange}
        onClose={bindPickerOpenCloseHandler('durationPicker', false)}
        onOpen={bindPickerOpenCloseHandler('durationPicker', true)}
      />,
    ],
    [
      autoFocus,
      bindPickerOpenCloseHandler,
      borderless,
      datePickerFormat,
      handleCheckInChange,
      handleDurationChange,
      handleStartDateChange,
      isDisabled,
      locale,
      name,
      openPicker,
      parsedDateRange.checkIn,
      parsedDateRange.durationValue,
      parsedDateRange.startDate,
      parsedMinDate,
      timePickerFormat,
      translate,
      unitsProps,
    ]
  );

  return (
    <FlexBox
      alignItems="center"
      flexDirection="row"
      gap={borderless ? 0 : 2}
      justifyContent="center"
      minWidth="80px"
      {...restProps}
    >
      {pickerNodes}
    </FlexBox>
  );
};

export default DateRangeDurationPicker;
